import cytoscape, {Core, Position} from 'cytoscape';
import * as React from 'react';
import cytoscapePopper from 'cytoscape-popper';
import styles from './graph.module.css';

import {GraphLayout, GraphNode, GraphEdge} from '../interface';
import {useDynamicStyles} from '../graph-rendering/use-dynamic-styles';
import {
    graphElementId,
    useCytoscapeInitialization
} from '../graph-rendering/use-cytoscape-initialization';
import {ControlPanel} from './control-panel';
import {useHiddenNodesActions} from '../graph-logic/hidden-nodes-actions/use-hidden-nodes-actions';
import {useGraphStateDiff} from '../graph-rendering/use-graph-state-diff';
import {useUndoRedo} from '../../../application/query-workflow/query-layout/completed-query/results-graph/use-undo-redo';
import {tippyFactory} from '../graph-rendering/popover';
import {DEFAULT_PADDING} from '../constants';

interface Props {
    nodePositions: GraphLayout;
    handleNodeMoved: (value: GraphLayout) => void;
    nodes: GraphNode[];
    edges: GraphEdge[];
    selectNodes: (nodeId: number[], isSelectMultiple: boolean) => void;
    selectedNodes: number[];
    hoveredNodes: number[];
    hiddenNodes: number[];
    setHoveredNodes: (nodeIds: number[]) => void;
    zoom: number;
    setZoom: (val: number) => void;
    pan: Position;
    setPan: (val: Position) => void;
    changesClearedOrSavedCounter?: number;
    hiddenNodesActions?: ReturnType<typeof useHiddenNodesActions>;
    undoRedoProps?: ReturnType<typeof useUndoRedo>;
}

cytoscape.use(cytoscapePopper(tippyFactory));

export const Graph = (props: Props) => {
    const {
        nodes,
        edges,
        selectNodes,
        selectedNodes,
        hoveredNodes,
        hiddenNodes,
        setHoveredNodes,
        nodePositions,
        handleNodeMoved,
        zoom,
        setZoom,
        pan,
        setPan,
        changesClearedOrSavedCounter,
        hiddenNodesActions
    } = props;
    const containerElementRef = React.useRef<HTMLDivElement>(null);
    const csRef = React.useRef<Core>(null);

    const focusGraph = React.useCallback(() => csRef.current?.fit(undefined, DEFAULT_PADDING), []);

    const graphStateDiffProps = useGraphStateDiff({
        selectedNodes,
        hoveredNodes,
        hiddenNodes,
        edges,
        isHiddenNodesVisible: hiddenNodesActions ? hiddenNodesActions.isHiddenNodesVisible : true,
        nodePositions
    });

    useCytoscapeInitialization({
        csRef,
        nodes,
        edges,
        nodePositions,
        handleNodeMoved,
        zoom,
        setZoom,
        pan,
        setPan,
        setHoveredNodes,
        selectNodes,
        changesClearedOrSavedCounter,
        getIsNodePartiallyHidden: graphStateDiffProps.getIsNodePartiallyHidden,
        getIsNodeFullyHidden: graphStateDiffProps.getIsNodeFullyHidden,
        getIsNodeSelected: graphStateDiffProps.getIsNodeSelected,
        getIsEdgeSelected: graphStateDiffProps.getIsEdgeSelected,
        getIsEdgePartiallyHidden: graphStateDiffProps.getIsEdgePartiallyHidden,
        getIsEdgeFullyHidden: graphStateDiffProps.getIsEdgeFullyHidden,
        isHiddenNodesVisible: hiddenNodesActions ? hiddenNodesActions.isHiddenNodesVisible : true
    });
    useDynamicStyles({
        csRef,
        containerElementRef,
        graphStateDiffProps,
        nodes,
        edges,
        nodePositions,
        zoom,
        pan
    });

    return (
        <div className={styles.graphContainer}>
            <div id={graphElementId} className={styles.graphView} ref={containerElementRef} />
            <ControlPanel
                focusGraph={focusGraph}
                hiddenNodesActions={hiddenNodesActions}
                undoRedoProps={props.undoRedoProps}
            />
        </div>
    );
};
